import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { PrismIconComponent } from '@shure/prism-angular-components';

export interface MuteStatusCellData {
	isMuted?: boolean;
}

export type MuteStatusRendererComponentParams = ICellRendererParams<object, MuteStatusCellData>;

@Component({
	selector: 'sh-mute-status-renderer',
	imports: [CommonModule, PrismIconComponent, TranslocoModule],
	templateUrl: './mute-status-renderer.component.html',
	styleUrl: './mute-status-renderer.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MuteStatusRendererComponent implements ICellRendererAngularComp {
	public readonly data = signal<MuteStatusCellData | null | undefined>(undefined);
	public readonly isMuted = computed(() => this.data()?.isMuted === true);
	public readonly isUnmuted = computed(() => this.data()?.isMuted === false);
	public readonly muteUnsupported = computed(() => this.data()?.isMuted === undefined);

	public shouldRender = signal(true);

	public params!: MuteStatusRendererComponentParams;

	public agInit(params: MuteStatusRendererComponentParams): void {
		this.update(params);
	}

	public refresh(params: MuteStatusRendererComponentParams): boolean {
		this.update(params);
		return true;
	}

	private update(params: MuteStatusRendererComponentParams): void {
		this.params = params;
		this.data.set(params.value);

		// If the grid is grouped don't render anything, unless it's grouped by this column
		// Note: This doesn't apply to child rows of a group (these will always render), only the group row itself
		const isGrouped = params.node.group === true;
		const isGroupedOnThisColumn = this.isGroupedOnThisColumn();

		if (isGrouped && !isGroupedOnThisColumn) {
			this.shouldRender.set(false);
		}
	}

	private isGroupedOnThisColumn(): boolean {
		const colId = this.params.column?.getColId();
		if (!colId) {
			return false;
		}
		return this.params.node.groupData?.[colId] !== undefined;
	}
}
